import type { HttpClient, HttpRequestOptions, HttpResponse } from "./types.js";
import type { Url } from "../data-model/primitives.js";
import type { CookieJar } from "./cookie-jar.js";
import { hostnameOf } from "../utils/url.js";

export const DEFAULT_UA_POOL: readonly string[] = Object.freeze([
  "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36",
  "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/123.0.0.0 Safari/537.36 Edg/123.0.0.0",
  "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:124.0) Gecko/20100101 Firefox/124.0",
  "Mozilla/5.0 (Macintosh; Intel Mac OS X 14_4_1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36",
  "Mozilla/5.0 (Macintosh; Intel Mac OS X 14_4) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.4 Safari/605.1.15",
  "Mozilla/5.0 (Macintosh; Intel Mac OS X 14_4_1; rv:124.0) Gecko/20100101 Firefox/124.0",
]);

export class UserAgents {
  readonly #pool: readonly string[];
  #idx = 0;
  constructor(pool: readonly string[] = DEFAULT_UA_POOL) {
    if (pool.length === 0) {
      throw new Error("UserAgents: pool must be non-empty");
    }
    this.#pool = pool;
  }
  next(): string {
    const idx = this.#idx % this.#pool.length;
    this.#idx++;
    const ua = this.#pool[idx];
    if (ua === undefined) {
      throw new Error("UserAgents: invariant violated — pool became empty");
    }
    return ua;
  }
}

export class UserAgentRotatingHttpClient implements HttpClient {
  constructor(
    private readonly inner: HttpClient,
    private readonly uas: UserAgents,
    private readonly defaults?: Readonly<Record<string, string>>,
    private readonly cookieJar?: CookieJar,
  ) {}

  async get(url: Url, opts?: HttpRequestOptions): Promise<HttpResponse> {
    const host = hostnameOf(url);
    const jarRead = this.cookieJar?.read(host);
    const callerHasCookie = Object.keys(opts?.headers ?? {}).some(
      (k) => k.toLowerCase() === "cookie",
    );
    const userAgent = jarRead?.ua ?? this.uas.next();

    const headers: Record<string, string> = {
      "User-Agent": userAgent,
      Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
      "Accept-Language": "vi,en;q=0.8",
      ...this.defaults,
      ...opts?.headers,
    };

    if (jarRead?.cookieHeader && !callerHasCookie) {
      headers["Cookie"] = jarRead.cookieHeader;
    }

    return this.inner.get(url, { ...opts, headers });
  }
}
